import React, { useState, useRef, MouseEvent } from "react";
import { Layers, Zap, Handshake } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const TiltCard = ({
  children,
  className,
  index
}: {
  children: React.ReactNode;
  className?: string;
  index: number;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState({
    x: 0,
    y: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / centerY * -5;
    const rotateY = (x - centerX) / centerX * 5;
    setRotation({
      x: rotateX,
      y: rotateY
    });
  };
  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => {
    setIsHovering(false);
    setRotation({
      x: 0,
      y: 0
    });
  };
  return <div data-section-id="16188" ref={cardRef} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-index={index} className={`relative transition-all duration-200 ease-out preserve-3d group ${className}`} style={{
    transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale3d(${isHovering ? 1.02 : 1}, ${isHovering ? 1.02 : 1}, 1)`
  }}>
      {children}
    </div>;
};
export default function TheTransformationPillars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const pillars = [{
    title: "Return to Sport",
    description: "High-level rehab for competitive athletes in Monmouth County. We bridge the gap between clinical discharge and peak performance on the field or court.",
    icon: <Layers className="w-10 h-10 text-primary relative z-10" />,
    glowColor: "#3b456e"
  }, {
    title: "Lift without Pain",
    description: "Gym-based injury prevention and strength coaching. Master your mechanics and build resilience without the setback of nagging joint or muscle pain.",
    icon: <Zap className="w-10 h-10 text-secondary relative z-10" />,
    glowColor: "#466dad"
  }, {
    title: "Post-Op Recovery",
    description: "Specialized ACL, shoulder, and joint replacement rehab. We bring concierge clinical excellence to your home for a seamless, expert-led recovery process.",
    icon: <Handshake className="w-10 h-10 text-accent-foreground relative z-10" />,
    glowColor: "#51b7f5"
  }];
  return <section id="your-performance-goals" className="relative py-24 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none z-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Your Performance Goals
          </h2>
          <p className="text-lg text-muted-foreground font-medium">Reach your peak with doctoral-level physical therapy and 1-on-1 performance coaching designed to get you back to the sports you love, faster. By bringing elite, personalized care directly to your door, we eliminate the logistics so you can focus entirely on your recovery and your results.</p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-3 gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {pillars.map((pillar, i) => <TiltCard key={i} index={i} className="h-full">
              <Card className="h-full bg-card/50 backdrop-blur-md border border-border/50 hover:border-accent/40 transition-colors duration-500 overflow-hidden shadow-xl hover:shadow-[0_20px_50px_-12px_rgba(81,183,245,0.2)] group">
                {}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none" style={{
              background: `radial-gradient(circle at 50% 0%, ${pillar.glowColor}, transparent 70%)`
            }} />

                <CardHeader className="relative z-10 pt-10 pb-2">
                  <div className="mb-6 relative inline-flex items-center justify-center w-20 h-20 rounded-xl bg-background border border-border shadow-sm group-hover:scale-110 transition-transform duration-500 ease-out">
                    {}
                    <div className="absolute inset-0 rounded-xl opacity-10 blur-sm transition-opacity duration-500 group-hover:opacity-30" style={{
                  backgroundColor: pillar.glowColor
                }} />
                    {pillar.icon}
                  </div>
                  <CardTitle className="text-2xl font-bold text-primary group-hover:text-secondary transition-colors duration-300">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="relative z-10">
                  <CardDescription className="text-base leading-relaxed text-muted-foreground group-hover:text-foreground transition-all duration-500 transform group-hover:-translate-y-1">
                    {pillar.description}
                  </CardDescription>
                </CardContent>

                {}
                <div className="absolute bottom-0 left-0 h-1.5 w-0 group-hover:w-full transition-all duration-700 ease-in-out" style={{
              backgroundColor: pillar.glowColor
            }} />
              </Card>
            </TiltCard>)}
        </div>
      </div>
    </section>;
}